import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { authClient } from '@yoreflix/core/api/authClient'
import type { AuthUser } from '@yoreflix/core/api/types'

/**
 * The provider dashboard's auth state.
 *
 * Identity is the SAME four-token viewer signin — there is no separate portal login (the whole
 * scheme, cookie rotation and CSRF, lives in `@yoreflix/core`'s authClient and is not restated
 * here). What the portal adds is the second question: this account authenticates, but may it use
 * THIS app? See `canUsePortal` in ./permissions.
 *
 * ⚠⚠ NO DEV BYPASS. `apps/web`'s AuthProvider carries a temporary bypass that has to be removed
 * before ship (docs: the pre-ship checklist), and it is exactly the kind of thing that gets
 * copied forward by pattern-matching. This portal is worse than the staff one to forge a session
 * in: it serves EXTERNAL companies their own commercial data, so a bypass here is a bypass of
 * the scoping contract itself. There is no env flag, no `import.meta.env` branch and no "local
 * only" shortcut in this file, and there must never be one. To test signin locally, sign in.
 */

export type AuthStatus = 'booting' | 'signed-out' | 'signed-in'

interface DashboardAuth {
  status: AuthStatus
  user: AuthUser | null
  signIn(email: string, password: string): Promise<void>
  signOut(): Promise<void>
}

const Ctx = createContext<DashboardAuth | null>(null)

export function useDashboardAuth(): DashboardAuth {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useDashboardAuth must be used inside <DashboardAuthProvider>')
  return ctx
}

export function DashboardAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('booting')
  const [user, setUser] = useState<AuthUser | null>(null)

  // The boot probe: a live session (the cookie spans *.yoreflix.uk, so arriving from the viewer
  // already signed in lands you signed in here too). Its own 401 is the expected "no session"
  // answer, not an error — authClient models that by returning null rather than throwing.
  useEffect(() => {
    let cancelled = false
    authClient
      .bootProbe()
      .then((u) => {
        if (cancelled) return
        setUser(u)
        setStatus(u ? 'signed-in' : 'signed-out')
      })
      .catch(() => {
        // A probe that fails for any OTHER reason (the API is down, CORS, offline) is not a
        // signed-in state. Fall back to signed-out: the sign-in screen is the one surface that
        // works without a session, and it will surface the real error when they try.
        if (cancelled) return
        setUser(null)
        setStatus('signed-out')
      })
    return () => {
      cancelled = true
    }
  }, [])

  // An unexpected 401 mid-session (expiry, revocation, refresh denied) — flush to signed-out so
  // the UI can't keep rendering a portal the server has stopped honouring.
  useEffect(() => {
    authClient.onUnauthorized(() => {
      setUser(null)
      setStatus('signed-out')
    })
    return () => authClient.onUnauthorized(null)
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const u = await authClient.signIn(email, password) // throws -> the form shows it
    setUser(u)
    setStatus('signed-in')
  }, [])

  const signOut = useCallback(async () => {
    try {
      await authClient.signOut()
    } finally {
      // Local state clears even if the revoke call failed: the session may well be gone
      // server-side, and leaving the UI "signed in" over a dead session is the worse outcome.
      setUser(null)
      setStatus('signed-out')
    }
  }, [])

  const value = useMemo<DashboardAuth>(() => ({ status, user, signIn, signOut }), [status, user, signIn, signOut])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
